import React, { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import { RECORDING_MODES } from "../utils/recordingModes";
import { getReviewMetadataPath } from "../utils/navigation/recordingModeNav";
import GuidanceAccordion from "../components/guidance/GuidanceAccordion";
import GuidanceBulletList from "../components/guidance/GuidanceBulletList";
import GuidanceImage from "../components/guidance/GuidanceImage";

function SpirometryGuidancePage() {
  const { t, i18n } = useTranslation();
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const guideLocale = lang.startsWith("ja") ? "ja" : "en-GB";

  const recordAgainPath = useMemo(
    () =>
      getReviewMetadataPath(
        "/recording/spirometry/camera",
        RECORDING_MODES.SPIROMETRY,
      ),
    [],
  );

  const positioningImage = `/guide_assets/RecordingSpirometry/${guideLocale}/01.png`;
  const manoeuvreImage = `/guide_assets/RecordingSpirometry/${guideLocale}/02.png`;

  return (
    <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto p-4">
      <section className="min-h-full flex flex-col gap-2">
        <header className="flex flex-col items-start text-start gap-1">
          <h1 className="subtitle-text">
            {t(
              "guidance.spirometry.title",
              "Improve your spirometry recording",
            )}
          </h1>
          <p className="">
            {t(
              "guidance.spirometry.intro",
              "Use these tips before repeating the recording.",
            )}
          </p>
        </header>

        <GuidanceAccordion
          title={t("guidance.spirometry.why.title", "Why a result may fail")}
          defaultOpen
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.spirometry.why.signal",
                "Signal quality can fail if the camera cannot clearly track your chest and abdomen movement, if your hands are not placed as instructed, or if clothing or lighting hides movement.",
              ),
              t(
                "guidance.spirometry.why.manoeuvre",
                "Spirometry quality can fail if the forced breath is too short, too gentle, or does not start from a full breath in.",
              ),
              t(
                "guidance.spirometry.why.overall",
                "Overall recording quality can fail if there is too much movement, poor lighting, camera shake, or an interrupted recording.",
              ),
            ]}
          />
        </GuidanceAccordion>

        <GuidanceAccordion
          title={t(
            "guidance.spirometry.recording.title",
            "Recording and camera tips",
          )}
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.spirometry.recording.stable",
                "Place the phone on a stable surface and keep it hands-free.",
              ),
              t(
                "guidance.spirometry.recording.frame",
                "Sit far enough back so your face, shoulders, chest, and upper abdomen remain visible.",
              ),
              t(
                "guidance.spirometry.recording.center",
                "Sit in the centre of the frame with your shoulders level.",
              ),
              t(
                "guidance.spirometry.recording.hands",
                "Keep one hand on your chest and one hand on your upper abdomen, with both hands visible.",
              ),
              t(
                "guidance.spirometry.recording.light",
                "Use steady lighting and avoid shadows, glare, or a bright window behind you.",
              ),
            ]}
          />
          <GuidanceImage
            src={positioningImage}
            alt={t(
              "guidance.spirometry.positioningImageAlt",
              "Correct spirometry camera position",
            )}
          />
        </GuidanceAccordion>

        <GuidanceAccordion
          title={t(
            "guidance.spirometry.manoeuvre.title",
            "Spirometry manoeuvre tips",
          )}
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.spirometry.manoeuvre.empty",
                "When prompted, breathe out fully first so your lungs feel empty.",
              ),
              t(
                "guidance.spirometry.manoeuvre.inhale",
                "Take a quick, deep breath in until your lungs feel completely full.",
              ),
              t(
                "guidance.spirometry.manoeuvre.blast",
                "Blow out hard and fast straight away, then keep blowing for as long as you can.",
              ),
              t(
                "guidance.spirometry.manoeuvre.long",
                "Try to continue the forced exhalation for around 6 seconds, unless you need to stop.",
              ),
              t(
                "guidance.spirometry.manoeuvre.still",
                "Keep your body still except for the breathing manoeuvre.",
              ),
            ]}
          />
          <GuidanceImage
            src={manoeuvreImage}
            alt={t(
              "guidance.spirometry.manoeuvreImageAlt",
              "Spirometry breathing manoeuvre instructions",
            )}
          />
        </GuidanceAccordion>

        <GuidanceAccordion
          title={t("guidance.spirometry.repeat.title", "Before trying again")}
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.spirometry.repeat.reset",
                "Take a moment to reset your position before starting another recording.",
              ),
              t(
                "guidance.spirometry.repeat.quiet",
                "Stay quiet and still before and after the forced breath.",
              ),
              t(
                "guidance.spirometry.repeat.prompt",
                "Follow the on-screen prompts and repeat the recording if the app asks you to.",
              ),
            ]}
          />
        </GuidanceAccordion>

        <div className="mt-auto w-full grid grid-cols-1 gap-2 pb-2">
          <Link
            to={recordAgainPath}
            className="primary-button w-full"
            onClick={() =>
              trackEvent("spirometry_guidance_click_record_again", {
                mode: RECORDING_MODES.SPIROMETRY,
              })
            }
          >
            {t("guidance.spirometry.recordAgain", "Record spirometry again")}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default memo(SpirometryGuidancePage);
